import { EntityType, Rights, calculateTimeDifferenceBetweenDates, sharedFromAccountName } from '@frontend/common';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { ModalType } from '@frontend/modal';
import { TransactionNivalizExtendedClient } from '@frontend/transaction/api';
import { TransactionStateBadge } from '@frontend/transaction/common';
import { ExtendedNivalizTransaction, TransactionState, TransactionStateName } from '@frontend/transaction/types';
import { FormattedMessage } from 'react-intl';

import {
    SHARED_COLUMN_HEADER,
    SHARED_COLUMN_HIDE,
    SHARED_COLUMN_ID,
    renderResolvedEntityCell,
    renderResolvedEntityListCell,
    renderSharedColumnCell
} from '../../../components/object-list-cells/object-list-cells.component';
import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { ApplicationCreateButton } from '../../components/create-button';
import OverviewHeader from '../../header/overview-header';
import { CreateTransactionModal } from '../../pudo/create-transaction-modal/create-transaction-modal.component';
import { TabFilter } from '../../tab-filter/tab-filter.component';
import { useCoffins } from './coffins.controller';

export const Coffins = () => {
    const viewProps = useCoffins();
    return (
        <div
            key='nivaliz-coffins'
            className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader
                title={
                    <FormattedMessage
                        id='feature.nivaliz.coffins'
                        defaultMessage='Coffins'
                    />
                }
                createButton={
                    <ApplicationCreateButton
                        modalId='nivaliz-create-transaction'
                        renderDefaultModal={() => (
                            <CreateTransactionModal
                                formId={viewProps.formId}
                                application={Application.NIVALIZ}
                                feature={Feature.INBOUND_TRANSACTION}
                            />
                        )}
                        modalOptions={{ type: ModalType.PANE, className: 'max-w-[600px]' }}
                        createPermissions={[{ item: EntityType.TRANSACTION, source: EntityType.ACCOUNT, right: Rights.CREATE, uid: '?' }]}
                        entity={CommonMessage.OBJECTS.TRANSACTION.DEFAULT}
                        functionality={viewProps.functionality}
                        wizardId={viewProps.functionalityWizardId}
                        requiresWizard
                        label={
                            <FormattedMessage
                                id='feature.transactions.create-transaction'
                                defaultMessage='Create transaction'
                            />
                        }
                    />
                }
            />
            <div className='inline-flex flex-col items-start justify-start gap-4 self-stretch'>
                <TabFilter
                    keys={['all', 'scheduled', 'awaiting_drop_off', 'awaiting_pick_up', 'completed', 'failed']}
                    displayMap={
                        new Map([
                            [
                                'all',
                                <FormattedMessage
                                    id='feature.transactions.tabs.all'
                                    defaultMessage='All'
                                    key='feature.transactions.tabs.all'
                                />
                            ],
                            [
                                'scheduled',
                                <FormattedMessage
                                    id='feature.nivaliz.coffins.tabs.scheduled'
                                    defaultMessage='Scheduled'
                                    key='feature.nivaliz.coffins.tabs.scheduled'
                                />
                            ],
                            [
                                'awaiting_drop_off',
                                <FormattedMessage
                                    id='feature.nivaliz.coffins.tabs.awaiting_drop_off'
                                    defaultMessage='Awaiting drop-off'
                                    key='feature.nivaliz.coffins.tabs.awaiting_drop_off'
                                />
                            ],
                            [
                                'awaiting_pick_up',
                                <FormattedMessage
                                    id='feature.nivaliz.coffins.tabs.awaiting_pick_up'
                                    defaultMessage='Awaiting pick-up'
                                    key='feature.nivaliz.coffins.tabs.awaiting_pick_up'
                                />
                            ],
                            [
                                'completed',
                                <FormattedMessage
                                    id='feature.nivaliz.coffins.tabs.completed'
                                    defaultMessage='Completed'
                                    key='feature.nivaliz.coffins.tabs.completed'
                                />
                            ],
                            [
                                'failed',
                                <FormattedMessage
                                    id='feature.nivaliz.coffins.tabs.failed'
                                    defaultMessage='Failed'
                                    key='feature.nivaliz.coffins.tabs.failed'
                                />
                            ]
                        ])
                    }
                    defaultActive='all'
                    onChange={viewProps.setActiveTab}
                    useQueryParams={{ key: 'tab' }}
                />
                <CoffinsList
                    key={viewProps.activeTab}
                    {...viewProps}
                />
            </div>
        </div>
    );
};

const CoffinsList = (viewProps: ReturnType<typeof useCoffins>) => {
    const { openPlatformDetail } = usePlatformModalHost();
    switch (viewProps.activeTab) {
        case 'all':
            return (
                <ReactiveObjectList<ExtendedNivalizTransaction>
                    key='all'
                    fetch={(arg) =>
                        TransactionNivalizExtendedClient.fetchExtendedNivalizTransactions({
                            account_id: viewProps.currentAccount,
                            application: Application.NIVALIZ,
                            feature: Feature.INBOUND_TRANSACTION,
                            state: TransactionState.ALL_EXCEPT_DRAFT.map((state) => state.value),
                            // Nivaliz UI doesn't render collis/packages/PIN-codes.
                            without_collis: 'true',
                            without_packages: 'true',
                            without_pin_codes: 'true',
                            ...arg
                        })
                    }
                    cardClassName='card bg-background-surface w-full border border-border-default'
                    dispatch={viewProps.dispatch}
                    hideColumns={SHARED_COLUMN_HIDE}
                    activeFilter={{
                        searchKeys: ['tracking_number', 'sender', 'deceased_name'],
                        filterKeys: ['spot_id', 'slot_id', 'state', 'after', 'before'],
                        displayName: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['sender', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['deceased_name', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['spot_id', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot_id', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS],
                            ['after', CommonMessage.FORMS.FROM_TIMESTAMP],
                            ['before', CommonMessage.FORMS.TO_TIMESTAMP]
                        ])
                    }}
                    sortingColumns={['tracking_number', 'creation_timestamp', 'state', 'waiting']}
                    reverseSortingColumns={['waiting']}
                    columns={{
                        columns: [
                            SHARED_COLUMN_ID,
                            'tracking_number',
                            'senders',
                            'deceased',
                            'state',
                            'waiting',
                            'spot',
                            'slot',
                            'schedule_stop_timestamp',
                            'creation_timestamp'
                        ],
                        headerOverwrite: new Map([
                            [SHARED_COLUMN_ID, SHARED_COLUMN_HEADER],

                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['deceased', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS],
                            ['waiting', CommonMessage.OBJECTS.TRANSACTION.WAITING_FOR],
                            ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            [
                                'schedule_stop_timestamp',
                                <FormattedMessage
                                    id='feature.nivaliz.transactions.expected-pick-up'
                                    description='Column label for expected pick-up date and time in Nivaliz transaction lists'
                                    defaultMessage='Expected pick-up'
                                />
                            ],
                            ['creation_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT]
                        ]),
                        customCells: new Map([
                            [SHARED_COLUMN_ID, (obj) => renderSharedColumnCell(obj.row.original.shared)],
                            [
                                'deceased',
                                (obj) =>
                                    renderResolvedEntityCell(obj.row.original.deceased, {
                                        entityTypeOverride: EntityType.DECEASED,
                                        parentShared: obj.row.original.shared,
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'senders',
                                (obj) =>
                                    renderResolvedEntityListCell(obj.row.original.senders, {
                                        entityTypeOverride: EntityType.USER,
                                        parentShared: obj.row.original.shared,
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'state',
                                (obj) => (
                                    <td
                                        key={obj.row.original.state}
                                        className='text-foreground-default truncate p-2 text-sm font-medium'>
                                        <TransactionStateBadge state={obj.row.original.state} />
                                    </td>
                                )
                            ],
                            [
                                'waiting',
                                (obj) => (
                                    <td
                                        key={obj.row.original.update_timestamp}
                                        className={defaultCellClassName}>
                                        {calculateTimeDifferenceBetweenDates(obj.row.original.update_timestamp, new Date(Date.now()).toISOString())}
                                    </td>
                                )
                            ],
                            [
                                'spot',
                                (obj) => (
                                    <td
                                        key={obj.row.original.spot?.entity_id}
                                        className={defaultCellClassName}>
                                        <PlatformObjectBadge
                                            id={obj.row.original.spot?.entity_id}
                                            display={obj.row.original.spot?.name}
                                            entityType={EntityType.SPOT}
                                            key={obj.row.original.spot?.entity_id}
                                            foreign={obj.row.original.spot?.foreign}
                                            accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                            accountName={sharedFromAccountName(obj.row.original.shared)}
                                        />
                                    </td>
                                )
                            ],
                            [
                                'slot',
                                (obj) => (
                                    <td
                                        key={obj.row.original.slot?.entity_id}
                                        className={defaultCellClassName}>
                                        {obj.row.original.slot ? (
                                            <PlatformObjectBadge
                                                id={obj.row.original.slot?.entity_id}
                                                display={obj.row.original.slot?.name}
                                                entityType={EntityType.SLOT}
                                                key={obj.row.original.slot?.entity_id}
                                                parentIds={{
                                                    spot_id: obj.row.original.slot.data.spot_id,
                                                    module_id: obj.row.original.slot.data.module_id
                                                }}
                                                foreign={obj.row.original.slot?.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.slot)}
                                                accountName={sharedFromAccountName(obj.row.original.shared)}
                                            />
                                        ) : (
                                            <span>/</span>
                                        )}
                                    </td>
                                )
                            ]
                        ])
                    }}
                    type={EntityType.TRANSACTION}
                    eventListener={EntityType.TRANSACTION}
                    showCount
                    id='all-coffins-list'
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.NIVALIZ } })
                    }
                />
            );
        case 'scheduled':
            return (
                <ReactiveObjectList<ExtendedNivalizTransaction>
                    key='scheduled'
                    fetch={(arg) =>
                        TransactionNivalizExtendedClient.fetchExtendedNivalizTransactions({
                            account_id: viewProps.currentAccount,
                            application: Application.NIVALIZ,
                            feature: Feature.INBOUND_TRANSACTION,
                            state: TransactionStateName.TRANSACTION_SCHEDULED,
                            // Nivaliz UI doesn't render collis/packages/PIN-codes.
                            without_collis: 'true',
                            without_packages: 'true',
                            without_pin_codes: 'true',
                            ...arg
                        })
                    }
                    cardClassName='card bg-background-surface w-full border border-border-default'
                    dispatch={viewProps.dispatch}
                    hideColumns={SHARED_COLUMN_HIDE}
                    activeFilter={{
                        searchKeys: ['tracking_number', 'sender', 'deceased_name'],
                        filterKeys: ['spot_id', 'slot_id', 'after', 'before'],
                        displayName: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['sender', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['deceased_name', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['spot_id', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot_id', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            ['after', CommonMessage.FORMS.FROM_TIMESTAMP],
                            ['before', CommonMessage.FORMS.TO_TIMESTAMP]
                        ])
                    }}
                    sortingColumns={['tracking_number', 'creation_timestamp', 'waiting']}
                    reverseSortingColumns={['waiting']}
                    columns={{
                        columns: [
                            SHARED_COLUMN_ID,
                            'tracking_number',
                            'senders',
                            'deceased',
                            'waiting',
                            'spot',
                            'slot',
                            'schedule_stop_timestamp',
                            'creation_timestamp'
                        ],
                        headerOverwrite: new Map([
                            [SHARED_COLUMN_ID, SHARED_COLUMN_HEADER],

                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['deceased', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['waiting', CommonMessage.OBJECTS.TRANSACTION.WAITING_FOR],
                            ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            [
                                'schedule_stop_timestamp',
                                <FormattedMessage
                                    id='feature.nivaliz.transactions.expected-pick-up'
                                    description='Column label for expected pick-up date and time in Nivaliz transaction lists'
                                    defaultMessage='Expected pick-up'
                                />
                            ],
                            ['creation_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT]
                        ]),
                        customCells: new Map([
                            [SHARED_COLUMN_ID, (obj) => renderSharedColumnCell(obj.row.original.shared)],
                            [
                                'deceased',
                                (obj) =>
                                    renderResolvedEntityCell(obj.row.original.deceased, {
                                        entityTypeOverride: EntityType.DECEASED,
                                        parentShared: obj.row.original.shared,
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'senders',
                                (obj) =>
                                    renderResolvedEntityListCell(obj.row.original.senders, {
                                        entityTypeOverride: EntityType.USER,
                                        parentShared: obj.row.original.shared,
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'waiting',
                                (obj) => (
                                    <td
                                        key={obj.row.original.update_timestamp}
                                        className={defaultCellClassName}>
                                        {calculateTimeDifferenceBetweenDates(obj.row.original.update_timestamp, new Date(Date.now()).toISOString())}
                                    </td>
                                )
                            ],
                            [
                                'spot',
                                (obj) => (
                                    <td
                                        key={obj.row.original.spot?.entity_id}
                                        className={defaultCellClassName}>
                                        <PlatformObjectBadge
                                            id={obj.row.original.spot?.entity_id}
                                            display={obj.row.original.spot?.name}
                                            entityType={EntityType.SPOT}
                                            key={obj.row.original.spot?.entity_id}
                                            foreign={obj.row.original.spot?.foreign}
                                            accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                            accountName={sharedFromAccountName(obj.row.original.shared)}
                                        />
                                    </td>
                                )
                            ],
                            [
                                'slot',
                                (obj) => (
                                    <td
                                        key={obj.row.original.slot?.entity_id}
                                        className={defaultCellClassName}>
                                        {obj.row.original.slot ? (
                                            <PlatformObjectBadge
                                                id={obj.row.original.slot?.entity_id}
                                                display={obj.row.original.slot?.name}
                                                entityType={EntityType.SLOT}
                                                key={obj.row.original.slot?.entity_id}
                                                parentIds={{
                                                    spot_id: obj.row.original.slot.data.spot_id,
                                                    module_id: obj.row.original.slot.data.module_id
                                                }}
                                                foreign={obj.row.original.slot?.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.slot)}
                                                accountName={sharedFromAccountName(obj.row.original.shared)}
                                            />
                                        ) : (
                                            <span>/</span>
                                        )}
                                    </td>
                                )
                            ]
                        ])
                    }}
                    type={EntityType.TRANSACTION}
                    eventListener={EntityType.TRANSACTION}
                    showCount
                    id='scheduled-coffins-list'
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.NIVALIZ } })
                    }
                />
            );
        case 'awaiting_drop_off':
            return (
                <ReactiveObjectList<ExtendedNivalizTransaction>
                    key='awaiting_drop_off'
                    fetch={(arg) =>
                        TransactionNivalizExtendedClient.fetchExtendedNivalizTransactions({
                            account_id: viewProps.currentAccount,
                            application: Application.NIVALIZ,
                            feature: Feature.INBOUND_TRANSACTION,
                            state: TransactionStateName.TRANSACTION_DROP_OFF_PENDING,
                            // Nivaliz UI doesn't render collis/packages/PIN-codes.
                            without_collis: 'true',
                            without_packages: 'true',
                            without_pin_codes: 'true',
                            ...arg
                        })
                    }
                    cardClassName='card bg-background-surface w-full border border-border-default'
                    dispatch={viewProps.dispatch}
                    hideColumns={SHARED_COLUMN_HIDE}
                    activeFilter={{
                        searchKeys: ['tracking_number', 'sender', 'deceased_name'],
                        filterKeys: ['spot_id', 'slot_id', 'after', 'before'],
                        displayName: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['sender', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['deceased_name', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['spot_id', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot_id', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            ['after', CommonMessage.FORMS.FROM_TIMESTAMP],
                            ['before', CommonMessage.FORMS.TO_TIMESTAMP]
                        ])
                    }}
                    sortingColumns={['tracking_number', 'creation_timestamp', 'waiting']}
                    reverseSortingColumns={['waiting']}
                    columns={{
                        columns: [
                            SHARED_COLUMN_ID,
                            'tracking_number',
                            'senders',
                            'deceased',
                            'waiting',
                            'spot',
                            'slot',
                            'schedule_stop_timestamp',
                            'creation_timestamp'
                        ],
                        headerOverwrite: new Map([
                            [SHARED_COLUMN_ID, SHARED_COLUMN_HEADER],

                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['deceased', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['waiting', CommonMessage.OBJECTS.TRANSACTION.WAITING_FOR],
                            ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            [
                                'schedule_stop_timestamp',
                                <FormattedMessage
                                    id='feature.nivaliz.transactions.expected-pick-up'
                                    description='Column label for expected pick-up date and time in Nivaliz transaction lists'
                                    defaultMessage='Expected pick-up'
                                />
                            ],
                            ['creation_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT]
                        ]),
                        customCells: new Map([
                            [SHARED_COLUMN_ID, (obj) => renderSharedColumnCell(obj.row.original.shared)],
                            [
                                'deceased',
                                (obj) =>
                                    renderResolvedEntityCell(obj.row.original.deceased, {
                                        entityTypeOverride: EntityType.DECEASED,
                                        parentShared: obj.row.original.shared,
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'senders',
                                (obj) =>
                                    renderResolvedEntityListCell(obj.row.original.senders, {
                                        entityTypeOverride: EntityType.USER,
                                        parentShared: obj.row.original.shared,
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'waiting',
                                (obj) => (
                                    <td
                                        key={obj.row.original.update_timestamp}
                                        className={defaultCellClassName}>
                                        {calculateTimeDifferenceBetweenDates(obj.row.original.update_timestamp, new Date(Date.now()).toISOString())}
                                    </td>
                                )
                            ],
                            [
                                'spot',
                                (obj) => (
                                    <td
                                        key={obj.row.original.spot?.entity_id}
                                        className={defaultCellClassName}>
                                        <PlatformObjectBadge
                                            id={obj.row.original.spot?.entity_id}
                                            display={obj.row.original.spot?.name}
                                            entityType={EntityType.SPOT}
                                            key={obj.row.original.spot?.entity_id}
                                            foreign={obj.row.original.spot?.foreign}
                                            accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                            accountName={sharedFromAccountName(obj.row.original.shared)}
                                        />
                                    </td>
                                )
                            ],
                            [
                                'slot',
                                (obj) => (
                                    <td
                                        key={obj.row.original.slot?.entity_id}
                                        className={defaultCellClassName}>
                                        {obj.row.original.slot ? (
                                            <PlatformObjectBadge
                                                id={obj.row.original.slot?.entity_id}
                                                display={obj.row.original.slot?.name}
                                                entityType={EntityType.SLOT}
                                                key={obj.row.original.slot?.entity_id}
                                                parentIds={{
                                                    spot_id: obj.row.original.slot.data.spot_id,
                                                    module_id: obj.row.original.slot.data.module_id
                                                }}
                                                foreign={obj.row.original.slot?.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.slot)}
                                                accountName={sharedFromAccountName(obj.row.original.shared)}
                                            />
                                        ) : (
                                            <span>/</span>
                                        )}
                                    </td>
                                )
                            ]
                        ])
                    }}
                    type={EntityType.TRANSACTION}
                    eventListener={EntityType.TRANSACTION}
                    showCount
                    id='awaiting_drop_off-coffins-list'
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.NIVALIZ } })
                    }
                />
            );
        case 'awaiting_pick_up':
            return (
                <ReactiveObjectList<ExtendedNivalizTransaction>
                    key='awaiting_pick_up'
                    fetch={(arg) =>
                        TransactionNivalizExtendedClient.fetchExtendedNivalizTransactions({
                            account_id: viewProps.currentAccount,
                            application: Application.NIVALIZ,
                            feature: Feature.INBOUND_TRANSACTION,
                            state: TransactionStateName.TRANSACTION_PICK_UP_PENDING,
                            // Nivaliz UI doesn't render collis/packages/PIN-codes.
                            without_collis: 'true',
                            without_packages: 'true',
                            without_pin_codes: 'true',
                            ...arg
                        })
                    }
                    cardClassName='card bg-background-surface w-full border border-border-default'
                    dispatch={viewProps.dispatch}
                    hideColumns={SHARED_COLUMN_HIDE}
                    activeFilter={{
                        searchKeys: ['tracking_number', 'sender', 'deceased_name'],
                        filterKeys: ['spot_id', 'slot_id', 'after', 'before'],
                        displayName: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['sender', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['deceased_name', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['spot_id', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot_id', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            ['after', CommonMessage.FORMS.FROM_TIMESTAMP],
                            ['before', CommonMessage.FORMS.TO_TIMESTAMP]
                        ])
                    }}
                    sortingColumns={['tracking_number', 'creation_timestamp', 'waiting']}
                    reverseSortingColumns={['waiting']}
                    columns={{
                        columns: [
                            SHARED_COLUMN_ID,
                            'tracking_number',
                            'senders',
                            'deceased',
                            'waiting',
                            'spot',
                            'slot',
                            'schedule_stop_timestamp',
                            'creation_timestamp'
                        ],
                        headerOverwrite: new Map([
                            [SHARED_COLUMN_ID, SHARED_COLUMN_HEADER],

                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['deceased', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['waiting', CommonMessage.OBJECTS.TRANSACTION.WAITING_FOR],
                            ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            [
                                'schedule_stop_timestamp',
                                <FormattedMessage
                                    id='feature.nivaliz.transactions.expected-pick-up'
                                    description='Column label for expected pick-up date and time in Nivaliz transaction lists'
                                    defaultMessage='Expected pick-up'
                                />
                            ],
                            ['creation_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT]
                        ]),
                        customCells: new Map([
                            [SHARED_COLUMN_ID, (obj) => renderSharedColumnCell(obj.row.original.shared)],
                            [
                                'deceased',
                                (obj) =>
                                    renderResolvedEntityCell(obj.row.original.deceased, {
                                        entityTypeOverride: EntityType.DECEASED,
                                        parentShared: obj.row.original.shared,
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'senders',
                                (obj) =>
                                    renderResolvedEntityListCell(obj.row.original.senders, {
                                        entityTypeOverride: EntityType.USER,
                                        parentShared: obj.row.original.shared,
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'waiting',
                                (obj) => (
                                    <td
                                        key={obj.row.original.update_timestamp}
                                        className={defaultCellClassName}>
                                        {calculateTimeDifferenceBetweenDates(obj.row.original.update_timestamp, new Date(Date.now()).toISOString())}
                                    </td>
                                )
                            ],
                            [
                                'spot',
                                (obj) => (
                                    <td
                                        key={obj.row.original.spot?.entity_id}
                                        className={defaultCellClassName}>
                                        <PlatformObjectBadge
                                            id={obj.row.original.spot?.entity_id}
                                            display={obj.row.original.spot?.name}
                                            entityType={EntityType.SPOT}
                                            key={obj.row.original.spot?.entity_id}
                                            foreign={obj.row.original.spot?.foreign}
                                            accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                            accountName={sharedFromAccountName(obj.row.original.shared)}
                                        />
                                    </td>
                                )
                            ],
                            [
                                'slot',
                                (obj) => (
                                    <td
                                        key={obj.row.original.slot?.entity_id}
                                        className={defaultCellClassName}>
                                        {obj.row.original.slot ? (
                                            <PlatformObjectBadge
                                                id={obj.row.original.slot?.entity_id}
                                                display={obj.row.original.slot?.name}
                                                entityType={EntityType.SLOT}
                                                key={obj.row.original.slot?.entity_id}
                                                parentIds={{
                                                    spot_id: obj.row.original.slot.data.spot_id,
                                                    module_id: obj.row.original.slot.data.module_id
                                                }}
                                                foreign={obj.row.original.slot?.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.slot)}
                                                accountName={sharedFromAccountName(obj.row.original.shared)}
                                            />
                                        ) : (
                                            <span>/</span>
                                        )}
                                    </td>
                                )
                            ]
                        ])
                    }}
                    type={EntityType.TRANSACTION}
                    eventListener={EntityType.TRANSACTION}
                    showCount
                    id='awaiting_pick_up-coffins-list'
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.NIVALIZ } })
                    }
                />
            );
        case 'completed':
            return (
                <ReactiveObjectList<ExtendedNivalizTransaction>
                    key='completed'
                    fetch={(arg) =>
                        TransactionNivalizExtendedClient.fetchExtendedNivalizTransactions({
                            account_id: viewProps.currentAccount,
                            application: Application.NIVALIZ,
                            feature: Feature.INBOUND_TRANSACTION,
                            state: [
                                TransactionStateName.TRANSACTION_COMPLETED,
                                TransactionStateName.TRANSACTION_COMPLETED_WITH_FAILURES,
                                TransactionStateName.TRANSACTION_CANCELLED
                            ],
                            // Nivaliz UI doesn't render collis/packages/PIN-codes.
                            without_collis: 'true',
                            without_packages: 'true',
                            without_pin_codes: 'true',
                            ...arg
                        })
                    }
                    cardClassName='card bg-background-surface w-full border border-border-default'
                    dispatch={viewProps.dispatch}
                    hideColumns={SHARED_COLUMN_HIDE}
                    activeFilter={{
                        searchKeys: ['tracking_number', 'sender', 'deceased_name'],
                        filterKeys: ['spot_id', 'slot_id', 'before', 'after'],
                        displayName: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['sender', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['deceased_name', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['spot_id', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot_id', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            ['before', CommonMessage.OBJECTS.COMMON_FIELDS.BEFORE],
                            ['after', CommonMessage.OBJECTS.COMMON_FIELDS.AFTER]
                        ])
                    }}
                    sortingColumns={['tracking_number', 'state', 'creation_timestamp']}
                    columns={{
                        columns: [SHARED_COLUMN_ID, 'tracking_number', 'senders', 'deceased', 'spot', 'slot', 'creation_timestamp'],
                        headerOverwrite: new Map([
                            [SHARED_COLUMN_ID, SHARED_COLUMN_HEADER],

                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['deceased', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            ['creation_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT]
                        ]),
                        customCells: new Map([
                            [SHARED_COLUMN_ID, (obj) => renderSharedColumnCell(obj.row.original.shared)],
                            [
                                'deceased',
                                (obj) =>
                                    renderResolvedEntityCell(obj.row.original.deceased, {
                                        entityTypeOverride: EntityType.DECEASED,
                                        parentShared: obj.row.original.shared,
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'senders',
                                (obj) =>
                                    renderResolvedEntityListCell(obj.row.original.senders, {
                                        entityTypeOverride: EntityType.USER,
                                        parentShared: obj.row.original.shared,
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'spot',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.spot?.entity_id}
                                            className={defaultCellClassName}>
                                            <PlatformObjectBadge
                                                id={obj.row.original.spot?.entity_id}
                                                entityType={EntityType.SPOT}
                                                display={obj.row.original.spot?.name}
                                                key={obj.row.original.spot?.entity_id}
                                                foreign={obj.row.original.spot?.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                                accountName={sharedFromAccountName(obj.row.original.shared)}
                                            />
                                        </td>
                                    );
                                }
                            ],
                            [
                                'slot',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.slot?.entity_id}
                                            className={defaultCellClassName}>
                                            {obj.row.original.slot ? (
                                                <PlatformObjectBadge
                                                    id={obj.row.original.slot?.entity_id}
                                                    entityType={EntityType.SLOT}
                                                    display={obj.row.original.slot?.name}
                                                    key={obj.row.original.slot?.entity_id}
                                                    parentIds={{
                                                        spot_id: obj.row.original.slot.data.spot_id,
                                                        module_id: obj.row.original.slot.data.module_id
                                                    }}
                                                    foreign={obj.row.original.slot?.foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.slot)}
                                                    accountName={sharedFromAccountName(obj.row.original.shared)}
                                                />
                                            ) : (
                                                <span>/</span>
                                            )}
                                        </td>
                                    );
                                }
                            ]
                        ])
                    }}
                    type={EntityType.TRANSACTION}
                    eventListener={EntityType.TRANSACTION}
                    showCount
                    id='completed-coffins-list'
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.NIVALIZ } })
                    }
                />
            );
        case 'failed':
            return (
                <ReactiveObjectList<ExtendedNivalizTransaction>
                    key='failed'
                    fetch={(arg) =>
                        TransactionNivalizExtendedClient.fetchExtendedNivalizTransactions({
                            account_id: viewProps.currentAccount,
                            application: Application.NIVALIZ,
                            feature: Feature.INBOUND_TRANSACTION,
                            state: TransactionStateName.TRANSACTION_FAILED,
                            // Nivaliz UI doesn't render collis/packages/PIN-codes.
                            without_collis: 'true',
                            without_packages: 'true',
                            without_pin_codes: 'true',
                            ...arg
                        })
                    }
                    cardClassName='card bg-background-surface w-full border border-border-default'
                    dispatch={viewProps.dispatch}
                    hideColumns={SHARED_COLUMN_HIDE}
                    activeFilter={{
                        searchKeys: ['tracking_number', 'sender', 'deceased_name'],
                        filterKeys: ['spot_id', 'slot_id', 'waiting'],
                        displayName: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['spot_id', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot_id', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            ['sender', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['deceased_name', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['waiting', CommonMessage.OBJECTS.TRANSACTION.WAITING_FOR]
                        ])
                    }}
                    sortingColumns={['tracking_number', 'creation_timestamp']}
                    columns={{
                        columns: [SHARED_COLUMN_ID, 'tracking_number', 'senders', 'deceased', 'spot', 'slot', 'creation_timestamp'],
                        headerOverwrite: new Map([
                            [SHARED_COLUMN_ID, SHARED_COLUMN_HEADER],

                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['deceased', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            ['creation_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT]
                        ]),
                        customCells: new Map([
                            [SHARED_COLUMN_ID, (obj) => renderSharedColumnCell(obj.row.original.shared)],
                            [
                                'deceased',
                                (obj) =>
                                    renderResolvedEntityCell(obj.row.original.deceased, {
                                        entityTypeOverride: EntityType.DECEASED,
                                        parentShared: obj.row.original.shared,
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'senders',
                                (obj) =>
                                    renderResolvedEntityListCell(obj.row.original.senders, {
                                        entityTypeOverride: EntityType.USER,
                                        parentShared: obj.row.original.shared,
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'spot',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.spot?.entity_id}
                                            className={defaultCellClassName}>
                                            <PlatformObjectBadge
                                                id={obj.row.original.spot?.entity_id}
                                                entityType={EntityType.SPOT}
                                                display={obj.row.original.spot?.name}
                                                key={obj.row.original.spot?.entity_id}
                                                foreign={obj.row.original.spot?.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                                accountName={sharedFromAccountName(obj.row.original.shared)}
                                            />
                                        </td>
                                    );
                                }
                            ],
                            [
                                'slot',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.slot?.entity_id}
                                            className={defaultCellClassName}>
                                            {obj.row.original.slot ? (
                                                <PlatformObjectBadge
                                                    id={obj.row.original.slot?.entity_id}
                                                    entityType={EntityType.SLOT}
                                                    display={obj.row.original.slot?.name}
                                                    key={obj.row.original.slot?.entity_id}
                                                    parentIds={{
                                                        spot_id: obj.row.original.slot.data.spot_id,
                                                        module_id: obj.row.original.slot.data.module_id
                                                    }}
                                                    foreign={obj.row.original.slot?.foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.slot)}
                                                    accountName={sharedFromAccountName(obj.row.original.shared)}
                                                />
                                            ) : (
                                                <span>/</span>
                                            )}
                                        </td>
                                    );
                                }
                            ]
                        ])
                    }}
                    type={EntityType.TRANSACTION}
                    eventListener={EntityType.TRANSACTION}
                    showCount
                    id='failed-coffins-list'
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.NIVALIZ } })
                    }
                />
            );
    }
};
